import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Package, Plus, Save, Loader2, Trash2, Edit2, Eye, EyeOff, Upload,
  MessageCircle, Tag, DollarSign
} from 'lucide-react';
import { API_URL, authApi } from '@/lib/api';

const emptyForm = {
  id: null,
  title: '',
  category: '',
  description: '',
  price: '',
  price_label: '',
  image: '',
  whatsapp: '',
  contact: '',
  status: 'published',
};

const CATEGORIES = ['Products', 'Services', 'Plants', 'Food', 'Consultation', 'Workshop', 'Other'];
const inputCls = 'w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/60';

/**
 * Exhibitor Product/Service manager — CRUD (title, category, description,
 * price/label, image, WhatsApp, status). Renders inside the exhibitor
 * dashboard. Server enforces `company_id`/`booth_id` scoping.
 */
export default function ProductsManager({ boothNumber }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const notify = (msg, isError = false) => {
    setFlash({ msg, isError });
    setTimeout(() => setFlash(null), 4000);
  };
  const authHeaders = { Authorization: `Bearer ${authApi.getToken()}`, Accept: 'application/json' };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/exhibitor/products`, { headers: authHeaders });
      setItems(r.data.data || []);
    } catch (e) {
      notify(e.response?.data?.message || 'Could not load products', true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleImage = (file) => {
    if (!file) return;
    if (file.size > 800 * 1024) { notify('Image too large — max 800KB', true); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const reset = () => { setForm(emptyForm); setEditing(false); };

  const save = async () => {
    if (!form.title.trim()) { notify('Title is required', true); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        category: form.category || undefined,
        description: form.description || undefined,
        price: form.price ? Number(form.price) : undefined,
        price_label: form.price_label || undefined,
        image: form.image || undefined,
        whatsapp: form.whatsapp || undefined,
        contact: form.contact || undefined,
        status: form.status || 'published',
      };
      if (editing && form.id) {
        await axios.patch(`${API_URL}/exhibitor/products/${form.id}`, payload, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
        notify('Product updated');
      } else {
        await axios.post(`${API_URL}/exhibitor/products`, payload, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
        notify('Product added');
      }
      reset();
      fetchList();
    } catch (e) {
      notify(e.response?.data?.message || e.response?.data?.detail || 'Save failed', true);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/exhibitor/products/${id}`, { headers: authHeaders });
      fetchList();
    } catch (e) { notify(e.response?.data?.message || 'Delete failed', true); }
  };

  const togglePublish = async (p) => {
    const next = p.status === 'published' ? 'hidden' : 'published';
    try {
      await axios.patch(`${API_URL}/exhibitor/products/${p.id}`, { status: next }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      fetchList();
    } catch (e) { notify(e.response?.data?.message || 'Update failed', true); }
  };

  const startEdit = (p) => {
    setForm({
      id: p.id, title: p.title || p.name || '', category: p.category || '',
      description: p.description || '', price: p.price ?? '', price_label: p.price_label || '',
      image: p.image || '', whatsapp: p.whatsapp || '', contact: p.contact || '',
      status: p.status || 'published',
    });
    setEditing(true);
    document.getElementById('products-manager-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 border border-slate-800" data-testid="products-manager">
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-4 h-4 text-cyan-400" />
        <div className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold">Products & Services</div>
        {boothNumber && <div className="ml-auto text-[10px] text-slate-500">Booth {boothNumber}</div>}
      </div>

      {flash && (
        <div className={`mb-3 px-3 py-2 rounded-lg text-xs font-semibold ${flash.isError ? 'bg-red-500/20 text-red-200 border border-red-500/40' : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'}`} data-testid="products-flash">
          {flash.msg}
        </div>
      )}

      {/* Add / Edit form */}
      <div id="products-manager-form" className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 mb-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Title (required)" className={inputCls} data-testid="product-title" />
          <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} className={inputCls} data-testid="product-category">
            <option value="">Category…</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={2} placeholder="Description" className={inputCls} data-testid="product-description" />
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="relative">
            <DollarSign className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2.5" />
            <input value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} type="number" min="0" step="0.01" placeholder="Price (RM)" className={`${inputCls} pl-7`} data-testid="product-price" />
          </div>
          <input value={form.price_label} onChange={(e) => setForm((p) => ({ ...p, price_label: e.target.value }))} placeholder='Price label (e.g. "FREE" or "From RM50")' className={inputCls} data-testid="product-price-label" />
          <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} className={inputCls} data-testid="product-status">
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.whatsapp} onChange={(e) => setForm((p) => ({ ...p, whatsapp: e.target.value }))} placeholder="WhatsApp (e.g. 60123456789)" className={inputCls} data-testid="product-whatsapp" />
          <input value={form.contact} onChange={(e) => setForm((p) => ({ ...p, contact: e.target.value }))} placeholder="Contact name or number" className={inputCls} data-testid="product-contact" />
        </div>

        {/* Image */}
        <div className="flex items-center gap-3">
          {form.image ? (
            <img src={form.image} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-slate-700" />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center">
              <Package className="w-6 h-6 text-slate-600" />
            </div>
          )}
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer">
            <Upload className="w-3.5 h-3.5" /> Upload image
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} data-testid="product-image-input" />
          </label>
          {form.image && (
            <button onClick={() => setForm((p) => ({ ...p, image: '' }))} className="text-xs text-red-300 hover:underline">Clear</button>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="product-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {editing ? 'Update' : 'Add product'}
          </button>
          {editing && (
            <button onClick={reset} className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold">Cancel</button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-slate-500 text-xs">Loading products…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-xs italic">No products yet — add your first one above.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="products-list">
          {items.map((p) => (
            <div key={p.id} className="p-3 rounded-xl bg-slate-950/50 border border-slate-800 flex gap-3">
              {p.image ? (
                <img src={p.image} alt={p.title} className="w-16 h-16 rounded-lg object-cover border border-slate-700 flex-none" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center flex-none">
                  <Package className="w-6 h-6 text-slate-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="text-white font-bold text-sm truncate" title={p.title}>{p.title || p.name}</div>
                  <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                    p.status === 'published' ? 'bg-emerald-500/20 text-emerald-300' :
                    p.status === 'draft' ? 'bg-slate-700 text-slate-300' :
                    'bg-red-500/20 text-red-300'
                  }`}>{p.status || 'published'}</span>
                </div>
                {p.category && <div className="text-[10px] text-slate-400 flex items-center gap-1"><Tag className="w-3 h-3" /> {p.category}</div>}
                {(p.price_label || p.price) && <div className="text-[11px] text-cyan-300 font-bold">{p.price_label || `RM ${p.price}`}</div>}
                {p.description && <div className="text-[11px] text-slate-400 line-clamp-2">{p.description}</div>}
                <div className="flex items-center gap-1 mt-2">
                  <button onClick={() => startEdit(p)} className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-bold flex items-center gap-0.5" data-testid={`product-edit-${p.id}`}><Edit2 className="w-3 h-3" /> Edit</button>
                  <button onClick={() => togglePublish(p)} className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-bold flex items-center gap-0.5" data-testid={`product-toggle-${p.id}`}>
                    {p.status === 'published' ? (<><EyeOff className="w-3 h-3" /> Hide</>) : (<><Eye className="w-3 h-3" /> Publish</>)}
                  </button>
                  <button onClick={() => remove(p.id)} className="px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 text-[10px] font-bold flex items-center gap-0.5" data-testid={`product-delete-${p.id}`}><Trash2 className="w-3 h-3" /> Delete</button>
                  {p.whatsapp && (
                    <a href={`https://wa.me/${p.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="ml-auto px-2 py-1 rounded bg-green-500/20 hover:bg-green-500/30 text-green-300 text-[10px] font-bold flex items-center gap-0.5">
                      <MessageCircle className="w-3 h-3" /> WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
